import { useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import DatePicker, { registerLocale } from 'react-datepicker';
import sv from 'date-fns/locale/sv';
import { InfoHint } from './info-hint.component';
import './employment-details.component.scss';

/* Register the Swedish locale with react-datepicker once, at module
   load. The library is built on date-fns and uses its locale system —
   the Swedish bundle contains every month / weekday name + short forms
   that the calendar header reads from. `date-fns/locale/sv` ships with
   react-datepicker's own dep tree, no extra install required. After
   this, passing `locale="sv"` on the <DatePicker> swaps every label
   (month names in the year dropdown, weekday headers, etc.) to
   Swedish. We DON'T register `en` because that's the library's
   built-in default — registering it would do nothing. */
registerLocale('sv', sv);

/* Portal the react-datepicker popper out of the DOM tree so it can't be
   clipped by the Employment Details card's `overflow: hidden` (needed
   for its rounded corners) — or by any ancestor's `transform`, which
   the CSS spec promotes to a containing block for `position: fixed`
   descendants, defeating the previous `popperProps.strategy: 'fixed'`
   escape on its own. Rendering the popper directly under <body> means
   neither constraint applies, and the calendar is free to drop into
   the viewport at its computed position. */
const PopperPortal: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    return ReactDOM.createPortal(children, document.body);
};

interface EmploymentDetailsProps {
    startDate: string;
    endDate: string;
    isCurrentlyWorking: boolean;
    onStartDateChange: (date: string) => void;
    onEndDateChange: (date: string) => void;
    onCurrentlyWorkingChange: (checked: boolean) => void;
    errors?: { startDate?: string; endDate?: string };
}

/* Bridges between the parent's ISO-string contract (`yyyy-mm-dd`,
   the format every consumer / API payload expects) and react-datepicker's
   `Date | null` model. Keeping the parent API unchanged means no upstream
   refactor — `onStartDateChange` / `onEndDateChange` still receive the
   same `yyyy-mm-dd` strings they did before. */
const parseIso = (s?: string): Date | null => {
    if (!s) return null;
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
};

const toIso = (d: Date | null): string => {
    if (!d) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

export const EmploymentDetails: React.FC<EmploymentDetailsProps> = ({
    startDate,
    endDate,
    isCurrentlyWorking,
    onStartDateChange,
    onEndDateChange,
    onCurrentlyWorkingChange,
    errors
}) => {
    const { t, i18n } = useTranslation();

    /* The active i18next language drives which locale the calendar
       renders in. i18next's `language` is the IETF tag we set in the
       URL (`en-us` / `sv-se`); the `.startsWith('sv')` check covers
       both `sv` and `sv-SE` casings. When Swedish is active the
       month names ("juni"), weekday headers ("må / ti / on / to / fr /
       lö / sö"), and screen-reader month strings all switch to
       Swedish; otherwise the library's default English locale is
       used. */
    const calendarLocale = (i18n.language || '').toLowerCase().startsWith('sv') ? 'sv' : undefined;

    return (
        <div className="employment-details">
            <div className="employment-details__card">
                <div className="employment-details__header">
                    <div className="ec-shead">
                        <span className="ec-sico" aria-hidden="true">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect width="18" height="18" x="3" y="4" rx="2" /><path d="M3 10h18M8 2v4M16 2v4" />
                            </svg>
                        </span>
                        <div className="ec-stext">
                            <h3 className="employment-details__title">{t('Employment Details')}</h3>
                            <p className="ec-sdesc">{t('Specify the employment period', { defaultValue: 'Specify the employment period' })}</p>
                        </div>
                    </div>
                </div>
                
                <div className="employment-details__body">
                <div className="employment-details__row">
                    <div className="employment-details__date-fields">
                        <div className="employment-details__date-input">
                            <label className="employment-details__date-label">
                                {t('Start date')}
                                <InfoHint
                                    text={t('The first day of the employment period', { defaultValue: 'The first day of the employment period' })}
                                />
                            </label>
                            <div className="employment-details__input-wrapper">
                                {/* Replaced native <input type="date"> with react-datepicker
                                    so the displayed format is guaranteed `yyyy/mm/dd` in
                                    every browser. The native picker's display format is
                                    OS / browser-locale controlled and ignores `placeholder`
                                    + `lang` is only a hint, so we use the library that's
                                    already installed (react-datepicker v4) and shipping
                                    CSS for it is already imported at src/index.js. The
                                    parent `onStartDateChange` callback still receives the
                                    same `yyyy-mm-dd` string contract via `toIso`. */}
                                <DatePicker
                                    selected={parseIso(startDate)}
                                    onChange={(d: Date | null) => onStartDateChange(toIso(d))}
                                    dateFormat="yyyy/MM/dd"
                                    placeholderText="yyyy/mm/dd"
                                    maxDate={new Date()}
                                    className={`employment-details__input${errors?.startDate ? ' employment-details__input--error' : ''}`}
                                    wrapperClassName="employment-details__input-dp"
                                    /* The Employment Details card has `overflow: hidden`
                                       (for its rounded corners) which was clipping the
                                       default `position: absolute` popper. `strategy: 'fixed'`
                                       renders the popper as `position: fixed` so it escapes
                                       any ancestor's overflow:hidden. The custom
                                       calendar/popper classNames let us theme the calendar
                                       in violet without affecting other react-datepicker
                                       instances elsewhere in the app. */
                                    /* Portal + fixed strategy together = the popper is
                                       rendered as `position: fixed` directly under <body>,
                                       guaranteed to escape any ancestor's overflow:hidden
                                       or transform-based containing block. */
                                    popperContainer={PopperPortal}
                                    popperProps={{ strategy: 'fixed' }}
                                    popperClassName="employment-details__dp-popper"
                                    calendarClassName="employment-details__dp-calendar"
                                    /* Disable typing — the only way to set a date is to
                                       click and pick from the calendar. We CAN'T use the
                                       `readOnly` prop here: in react-datepicker v4 that
                                       prop also disables the calendar trigger, so the
                                       picker wouldn't open on click. `onChangeRaw` fires
                                       on every raw input event (typing / paste);
                                       `preventDefault()` swallows it without affecting
                                       the picker's own setDate calls. Combined with
                                       `cursor: pointer` + `caret-color: transparent` in
                                       SCSS so the input doesn't look like a text field. */
                                    onChangeRaw={(e: React.SyntheticEvent | undefined) => e?.preventDefault()}
                                    /* Show month + year dropdowns in the calendar header
                                       so the user can jump straight to any month/year
                                       instead of clicking the chevron arrow many times.
                                       `dropdownMode="select"` renders them as native
                                       <select> elements — cleanest UX, fully keyboard-
                                       navigable, styled below in SCSS to match the
                                       violet header. */
                                    showMonthDropdown
                                    showYearDropdown
                                    /* `scroll` mode (vs `select`) makes react-datepicker render
                                       its OWN dropdown popup with regular divs we can style
                                       end-to-end. Native <select> popups are OS-controlled
                                       (blue highlight, system font, etc.) and can't be themed
                                       beyond `option { color, background }`. */
                                    dropdownMode="scroll"
                                    /* When app language is Swedish this is `'sv'`, otherwise
                                       undefined (so react-datepicker falls back to its
                                       default English). Localises month names + weekday
                                       headers + screen-reader labels in the calendar. */
                                    locale={calendarLocale}
                                    autoComplete="off"
                                />
                                <span className="employment-details__calendar-icon">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="17" height="19" viewBox="0 0 17 19" fill="none">
                                        <path d="M1 7.66667H16M5.16667 4.33333V1M11.8333 4.33333V1M6.33333 17.6667H10.6667C12.5335 17.6667 13.4669 17.6667 14.18 17.3034C14.8072 16.9838 15.3171 16.4738 15.6367 15.8466C16 15.1336 16 14.2002 16 12.3333V8C16 6.13316 16 5.19974 15.6367 4.4867C15.3171 3.85949 14.8072 3.34956 14.18 3.02998C13.4669 2.66667 12.5335 2.66667 10.6667 2.66667H6.33333C4.46649 2.66667 3.53307 2.66667 2.82003 3.02998C2.19282 3.34956 1.68289 3.85949 1.36331 4.4867C1 5.19974 1 6.13316 1 8V12.3333C1 14.2002 1 15.1336 1.36331 15.8466C1.68289 16.4738 2.19282 16.9838 2.82003 17.3034C3.53307 17.6667 4.46649 17.6667 6.33333 17.6667Z" stroke="#68727D" strokeWidth="2" strokeLinecap="round"/>
                                    </svg>
                                </span>
                            </div>
                            {errors?.startDate && <span className="employment-details__error-msg">{t(errors.startDate)}</span>}
                        </div>
                        
                        <div className="employment-details__date-input">
                            <label className="employment-details__date-label">
                                {t('End date')}
                                <InfoHint
                                    text={t('The last day of employment (leave empty if currently working)', { defaultValue: 'The last day of employment (leave empty if currently working)' })}
                                />
                            </label>
                            <div className="employment-details__input-wrapper">
                                {/* Same react-datepicker swap as the Start date input above —
                                    guaranteed yyyy/MM/dd display in every browser, same
                                    string contract on the onChange callback. */}
                                <DatePicker
                                    selected={parseIso(endDate)}
                                    onChange={(d: Date | null) => onEndDateChange(toIso(d))}
                                    dateFormat="yyyy/MM/dd"
                                    placeholderText="yyyy/mm/dd"
                                    maxDate={new Date()}
                                    className={`employment-details__input${errors?.endDate ? ' employment-details__input--error' : ''}`}
                                    wrapperClassName="employment-details__input-dp"
                                    /* Same popper escape + violet theme as the Start date input. */
                                    /* Portal + fixed strategy together = the popper is
                                       rendered as `position: fixed` directly under <body>,
                                       guaranteed to escape any ancestor's overflow:hidden
                                       or transform-based containing block. */
                                    popperContainer={PopperPortal}
                                    popperProps={{ strategy: 'fixed' }}
                                    popperClassName="employment-details__dp-popper"
                                    calendarClassName="employment-details__dp-calendar"
                                    /* Disable typing — the only way to set a date is to
                                       click and pick from the calendar. We CAN'T use the
                                       `readOnly` prop here: in react-datepicker v4 that
                                       prop also disables the calendar trigger, so the
                                       picker wouldn't open on click. `onChangeRaw` fires
                                       on every raw input event (typing / paste);
                                       `preventDefault()` swallows it without affecting
                                       the picker's own setDate calls. Combined with
                                       `cursor: pointer` + `caret-color: transparent` in
                                       SCSS so the input doesn't look like a text field. */
                                    onChangeRaw={(e: React.SyntheticEvent | undefined) => e?.preventDefault()}
                                    /* Show month + year dropdowns in the calendar header
                                       so the user can jump straight to any month/year
                                       instead of clicking the chevron arrow many times.
                                       `dropdownMode="select"` renders them as native
                                       <select> elements — cleanest UX, fully keyboard-
                                       navigable, styled below in SCSS to match the
                                       violet header. */
                                    showMonthDropdown
                                    showYearDropdown
                                    /* `scroll` mode (vs `select`) makes react-datepicker render
                                       its OWN dropdown popup with regular divs we can style
                                       end-to-end. Native <select> popups are OS-controlled
                                       (blue highlight, system font, etc.) and can't be themed
                                       beyond `option { color, background }`. */
                                    dropdownMode="scroll"
                                    /* When app language is Swedish this is `'sv'`, otherwise
                                       undefined (so react-datepicker falls back to its
                                       default English). Localises month names + weekday
                                       headers + screen-reader labels in the calendar. */
                                    locale={calendarLocale}
                                    autoComplete="off"
                                    disabled={isCurrentlyWorking}
                                />
                                <span className="employment-details__calendar-icon">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="17" height="19" viewBox="0 0 17 19" fill="none">
                                        <path d="M1 7.66667H16M5.16667 4.33333V1M11.8333 4.33333V1M6.33333 17.6667H10.6667C12.5335 17.6667 13.4669 17.6667 14.18 17.3034C14.8072 16.9838 15.3171 16.4738 15.6367 15.8466C16 15.1336 16 14.2002 16 12.3333V8C16 6.13316 16 5.19974 15.6367 4.4867C15.3171 3.85949 14.8072 3.34956 14.18 3.02998C13.4669 2.66667 12.5335 2.66667 10.6667 2.66667H6.33333C4.46649 2.66667 3.53307 2.66667 2.82003 3.02998C2.19282 3.34956 1.68289 3.85949 1.36331 4.4867C1 5.19974 1 6.13316 1 8V12.3333C1 14.2002 1 15.1336 1.36331 15.8466C1.68289 16.4738 2.19282 16.9838 2.82003 17.3034C3.53307 17.6667 4.46649 17.6667 6.33333 17.6667Z" stroke="#68727D" strokeWidth="2" strokeLinecap="round"/>
                                    </svg>
                                </span>
                            </div>
                            {errors?.endDate && <span className="employment-details__error-msg">{t(errors.endDate)}</span>}
                        </div>
                        
                        <div className="employment-details__checkbox">
                            <label className="employment-details__checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={isCurrentlyWorking}
                                    onChange={(e) => {
                                        onCurrentlyWorkingChange(e.target.checked);
                                        if (e.target.checked) {
                                            onEndDateChange('');
                                        }
                                    }}
                                    className="employment-details__checkbox-input"
                                />
                                <span>{t('Currently working')}</span>
                            </label>
                        </div>
                    </div>
                </div>
                </div>
            </div>
        </div>
    );
};
